import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import {
    Button,
    CircularProgress,
    Collapse,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
    Pagination,
    TextField,
} from '@mui/material';
import { KeyboardArrowDown, SearchOutlined } from '@mui/icons-material';

import { asGermanDateTime } from 'src/common/date';
import useDebouncer from 'src/common/useDebouncer';
import { PotentialAnalysisListItemDto } from './pv-analysis.dto';
import { admiPVAnalysisService } from './admi-pv-analysis.service';
import { PaginatedResponseDto } from 'src/infrastructure/admi-client/response.dto';

const SEARCH_DEBOUNCE_DELAY_MS = 500;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 15;

export default function PVAnalysisListPage() {
    const [pvAnalysis, setPVAnalysis] = useState<PaginatedResponseDto<PotentialAnalysisListItemDto>>();
    const [open, setOpen] = useState<{ [key: string]: boolean }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams({
        page: DEFAULT_PAGE_NUMBER.toString(),
        perPage: DEFAULT_PAGE_SIZE.toString(),
        searchString: '',
    });

    const { t } = useTranslation();
    const debounce = useDebouncer();

    useEffect(() => {
        loadPVAnalysis();
    }, [searchParams]);

    async function loadPVAnalysis() {
        setIsLoading(true);

        const page = Number(searchParams.get('page')) || DEFAULT_PAGE_NUMBER;
        const size = Number(searchParams.get('perPage')) || DEFAULT_PAGE_SIZE;
        const searchString = searchParams.get('searchString') || '';

        try {
            const response = await admiPVAnalysisService.listPVAnalysisIncludingVariants({
                page,
                size,
                searchString,
                include: ['variants'],
            });
            setPVAnalysis(response);
        } catch (error) {
            console.error(error);
        }
        setIsLoading(false);
    }

    function changePage(page: number) {
        setSearchParams((prev) => {
            const paramsObject = Object.fromEntries(prev.entries()); // Spread operator doesn't work directly on URLSearchParams
            return { ...paramsObject, page: page.toString() };
        });
    }

    async function handleDeleteVariant(uuid: string) {
        if (window.confirm(t('Are you sure you want to delete this variant?'))) {
            await admiPVAnalysisService.deletePVAnalysisVariant(uuid);
            loadPVAnalysis();
        }
    }

    function formatAnalysisTitle(analysis: PotentialAnalysisListItemDto): string {
        const variantAddress = analysis.variants?.[0]?.streetAddress;
        return variantAddress ? `${analysis.name} | ${variantAddress}` : analysis.name;
    }

    return (
        <Grid container direction="row" size={12} spacing={2}>
            <Grid size={12}>
                <Typography variant="h4">{t('Potential Analyses')}</Typography>
            </Grid>

            <Grid size={12}>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <TextField
                        variant="outlined"
                        size="small"
                        sx={{ width: 350 }}
                        placeholder={t('Search...')}
                        slotProps={{ input: { startAdornment: <SearchOutlined /> } }}
                        onChange={(e) =>
                            debounce(
                                () =>
                                    setSearchParams({
                                        page: DEFAULT_PAGE_NUMBER.toString(),
                                        perPage: searchParams.get('perPage') || DEFAULT_PAGE_SIZE.toString(),
                                        searchString: e.target.value,
                                    }),
                                SEARCH_DEBOUNCE_DELAY_MS,
                            )
                        }
                    />
                    <Link to="/pv-analysis/create">
                        <Button variant="contained">{t('Create New Potential Analysis')}</Button>
                    </Link>
                </Stack>
            </Grid>

            <Grid size={12}>
                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <List disablePadding>
                        {pvAnalysis?.content?.map((analysis) => (
                            <Fragment key={analysis.id}>
                                <ListItemButton
                                    sx={{ my: 1, pl: 0 }}
                                    onClick={() => setOpen({ ...open, [analysis.id]: !open[analysis.id] })}
                                >
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                        <KeyboardArrowDown
                                            sx={[
                                                open[analysis.id]
                                                    ? { transform: 'initial' }
                                                    : { transform: 'rotate(-90deg)' },
                                            ]}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={formatAnalysisTitle(analysis)}
                                        slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                    />
                                </ListItemButton>

                                <Collapse in={open[analysis.id]} timeout={100}>
                                    <List component="div" sx={{ pl: '2rem' }} disablePadding>
                                        <ListItem>
                                            <Table size="small">
                                                <TableBody>
                                                    {analysis.variants?.map((variant) => (
                                                        <TableRow key={variant.id}>
                                                            <TableCell width="40%">{variant.name}</TableCell>
                                                            <TableCell width="30%">{variant.author}</TableCell>
                                                            <TableCell width="15%">
                                                                {asGermanDateTime(variant.createdAt.toString())}
                                                            </TableCell>
                                                            <TableCell width="15%">
                                                                <Stack direction="row" spacing={1}>
                                                                    <Link
                                                                        to={`/pv-analysis/pv-analysis-variation/${variant.id}/details`}
                                                                    >
                                                                        <Button variant="contained">{t('Open')}</Button>
                                                                    </Link>
                                                                    <Button
                                                                        variant="contained"
                                                                        color="error"
                                                                        onClick={() => handleDeleteVariant(variant.id)}
                                                                    >
                                                                        {t('Delete')}
                                                                    </Button>
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </ListItem>
                                    </List>
                                </Collapse>
                            </Fragment>
                        ))}
                    </List>
                )}
            </Grid>

            <Grid size={12} container sx={{ justifyContent: 'center' }}>
                {pvAnalysis !== undefined && pvAnalysis.page.totalElements > 0 && (
                    <Pagination
                        variant="outlined"
                        count={pvAnalysis.page.totalPages}
                        page={pvAnalysis.page.number + 1}
                        onChange={(_, page) => changePage(page)}
                    />
                )}
            </Grid>
        </Grid>
    );
}
